// RegionsOpen — Stage 8: IA 主次倒置。
//
// 之前 tab 重心是「· 開 放 / 關 閉」 toggle、自我探索編輯藏在折疊
// 小 link 後面。實際工作流相反:**自我探索題目是活動前主要批次
// 編輯的事**、區域開關只是劇情推進偶爾切。tab 名已改「探索」
// (commit 68f96bb)、IA 跟著倒置:
//   - H1「自我探索」(原「區域開放」)
//   - 每 row textarea 永遠展開可編輯
//   - 「關閉/開放」按鈕降級成 row 右上角小 chip
//
// dirty + listener race:之前的 `expanded` flag 同時擔任「user 在
// 編輯 → 別 sync 覆蓋」的旗子。拿掉後改用 dirty ref:只在 draft
// === server promptsText 時才接受 server 的新值。

import {
  Hairline,
  Han,
  HUD,
  MonoData,
  MonoLabel,
  T,
  TextBtn,
} from "@goodbye/ui";
import { regionDoc, type RegionDoc, type WithId } from "@goodbye/firebase";
import { updateDoc } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import { useAdminSyncCtx } from "../context";

export function RegionsOpen() {
  const { regions, markers, loading } = useAdminSyncCtx();
  const openCount = regions.filter((r) => r.open).length;

  async function toggle(id: string, next: boolean) {
    await updateDoc(regionDoc(id), { open: next });
  }

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        background: T.paper,
        // 底部留白移到實際裝 region 卡片的內容層(見下方),這裡不再重複墊。
      }}
    >
      <HUD label="工 作 端" />
      <div
        style={{
          padding: "20px 32px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
          }}
        >
          <Han size={20} color={T.ink900}>自 我 探 索</Han>
          <MonoData size={11} color={T.ink500}>
            {openCount} / {regions.length} 已 開 放
          </MonoData>
        </div>
        <Han size={11} color={T.ink350} lh={1.8}>
          為 每 個 區 域 編 輯 探 索 題 目。 學 員 端 1-2 秒 內 看 到 更 新。
        </Han>
      </div>
      <Hairline style={{ margin: "8px 32px 0" }} />

      <div
        style={{
          padding: "0 32px",
          // 2026-05-30:最後一區的展開 textarea + 說明會被 fixed AdminNav
          // 蓋住。在「內容區自己」墊出 tab bar 高度的底部留白(bar ≈ 包含
          // padding 24 + 44 button + 細框 + safe-area),確保滑到底時最後一
          // 區完整露出、不被 tab 壓住。外層的 paddingBottom 對 flex column
          // 末端不夠可靠,改放在實際裝卡片的這層。
          paddingBottom:
            "calc(120px + env(safe-area-inset-bottom, 0px))",
        }}
      >
        {loading && <Han size={13} color={T.ink350}>連線中…</Han>}
        {regions.map((r) => {
          const placed = markers.filter((m) => m.regionId === r.id).length;
          return (
            <RegionRow
              key={r.id}
              region={r}
              placed={placed}
              onToggle={toggle}
            />
          );
        })}
      </div>

    </div>
  );
}

interface RegionRowProps {
  region: WithId<RegionDoc>;
  placed: number;
  onToggle: (id: string, next: boolean) => void | Promise<void>;
}

function RegionRow({ region, placed, onToggle }: RegionRowProps) {
  const r = region;
  const promptCount = r.prompts?.length ?? 0;
  const promptsText = (r.prompts ?? []).join("\n");

  const [draft, setDraft] = useState(promptsText);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const flashTimerRef = useRef<number | null>(null);

  // Stage 8: textarea 永遠展開。要避免「工作員正在打字、Firestore
  // listener 同步別人的改動就覆蓋掉自己」、用 dirty ref 把關:只在
  // draft === 上次同步的 server 值時才接受新 server 值。
  const lastServerRef = useRef(promptsText);
  useEffect(() => {
    if (draft === lastServerRef.current) {
      setDraft(promptsText);
    }
    lastServerRef.current = promptsText;
  }, [promptsText, draft]);

  useEffect(
    () => () => {
      if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    },
    [],
  );

  const dirty = draft !== promptsText;

  async function save() {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    const lines = draft
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      await updateDoc(regionDoc(r.id), { prompts: lines });
      setSavedFlash(true);
      if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
      flashTimerRef.current = window.setTimeout(() => setSavedFlash(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "儲存失敗");
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setDraft(promptsText);
    setError(null);
  }

  return (
    <div
      style={{
        padding: "20px 0",
        borderBottom: `1px solid ${T.ink100}`,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {/* Row header: 區名 + meta (左) + 小開關 chip (右上) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          alignItems: "flex-start",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Han size={17} color={r.open ? T.ink900 : T.ink500}>{r.name}</Han>
          <MonoLabel size={11} color={T.ink350}>
            {r.code} · {placed} marker · {promptCount} 題
          </MonoLabel>
        </div>
        {r.open ? (
          <TextBtn
            size={11}
            color={T.ink500}
            ls="0.10em"
            onClick={() => {
              if (window.confirm(`關閉「${r.name}」?學員端會立刻看不到此區。`)) {
                onToggle(r.id, false);
              }
            }}
            style={{
              padding: "4px 8px",
              border: `1px solid ${T.ink200}`,
              whiteSpace: "nowrap",
              minHeight: 0,
            }}
          >
            · 關 閉
          </TextBtn>
        ) : (
          <TextBtn
            size={11}
            color={T.ink900}
            ls="0.10em"
            onClick={() => onToggle(r.id, true)}
            style={{
              padding: "4px 8px",
              border: `1px solid ${T.ink200}`,
              whiteSpace: "nowrap",
              minHeight: 0,
            }}
          >
            · 開 放
          </TextBtn>
        )}
      </div>

      {/* Main edit area: textarea 永遠展開 */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          spellCheck={false}
          style={{
            fontFamily: T.han,
            fontSize: 14,
            lineHeight: 1.8,
            color: T.ink900,
            background: T.paper,
            border: `1px solid ${T.ink200}`,
            borderRadius: 0,
            padding: 12,
            minHeight: 120,
            resize: "vertical",
            width: "100%",
            boxSizing: "border-box",
          }}
          placeholder="例:在 這 條 街 第 一 眼 看 到 什 麼 ?"
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <Han size={10} color={T.ink350} style={{ flex: 1, minWidth: 0 }}>
            一 行 一 題 . 空 行 略 過 .
          </Han>
          {dirty && (
            <TextBtn
              size={12}
              color={!saving ? T.ink900 : T.ink350}
              ls="0.12em"
              onClick={save}
              disabled={saving}
              style={{
                padding: "6px 12px",
                border: `1px solid ${T.ink900}`,
                whiteSpace: "nowrap",
              }}
            >
              {saving ? "· 儲 存 中…" : "· 儲 存"}
            </TextBtn>
          )}
          {dirty && !saving && (
            <TextBtn
              size={11}
              color={T.ink500}
              ls="0.10em"
              onClick={cancel}
              style={{ padding: "6px 0", minHeight: 0 }}
            >
              · 取 消
            </TextBtn>
          )}
          {savedFlash && !dirty && (
            <MonoLabel size={10} color={T.ink500}>· 已 儲 存</MonoLabel>
          )}
          {error && (
            <MonoLabel size={10} color={T.ink900}>· {error}</MonoLabel>
          )}
        </div>
      </div>
    </div>
  );
}
